import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  Link as LinkIcon,
  Sparkles,
  Trash2,
  UserRound,
} from 'lucide-react';
import B2BLayout from '../../../components/b2b/B2BLayout';
import { Toast } from '../../../components/ui';
import { apiFetch } from '../../../services/apiClient';
import {
  ErrorBanner,
  PersonaAvatar,
  PersonaState,
  PersonaTagChip,
  normalizePersonaTag,
  statusLabel,
} from '../components/PersonaShared';
import PersonaResultHeader from '../components/PersonaResultHeader';
import { asDetailText, buildPersonaDetail } from '../services/personaDetail';
import { personaApi, personaAssetUrl, personaResultShareApi, personaTestApi } from '../services/personaService';
import { downloadExportFile, sanitizeCsvFilename } from '../utils/csvExport';
import '../styles/persona.scss';

const EXPORT_OPTIONS = [
  { value: 'csv', label: 'CSV (.csv)' },
  { value: 'xlsx', label: 'Excel (.xlsx)' },
];
const FEEDBACK_PAGE_SIZE = 3;

const formatResultDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

const formatSharedAt = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '공유 시각을 확인할 수 없습니다.';
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).replace(/\.\s?/g, '.').replace(/\.$/, '');
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const firstText = (...values) => values.find((value) => typeof value === 'string' && value.trim()) || '';

const getResultPersona = (result, personas) => {
  const snapshot = result?.personaSnapshot || result?.persona_snapshot || {};
  const personaId = result?.persona_id ?? result?.personaId;
  const matched = personas.find((persona) => String(persona.id) === String(personaId));
  return {
    ...matched,
    ...snapshot,
    id: snapshot.id || matched?.id || personaId,
    name: snapshot.name || matched?.name || (personaId ? `#${personaId}` : '퍼소나'),
  };
};

const getFeedbackSummary = (result) => {
  const feedback = result?.feedback;
  const scores = result?.scores || {};
  if (typeof feedback === 'string') return feedback;
  if (Array.isArray(feedback)) return feedback.join(' ');
  if (feedback && typeof feedback === 'object') {
    const screenFeedback = asArray(feedback.screenFeedbacks || feedback.screen_feedbacks)[0]?.feedback;
    return firstText(
      feedback.flowSummary,
      feedback.flow_summary,
      scores.flowSummary,
      scores.flow_summary,
      feedback.overallFeedback,
      feedback.overall_feedback,
      feedback.summary,
      feedback.reason,
      screenFeedback,
    );
  }
  return firstText(
    scores.flowSummary,
    scores.flow_summary,
    result?.summary,
    result?.error_message,
    '분석 결과가 아직 정리되지 않았습니다.',
  );
};

const getScore = (scores, keys, fallback = 0) => {
  for (const key of keys) {
    if (scores?.[key] !== undefined && scores?.[key] !== null) return scores[key];
  }
  return fallback;
};

const getNumericScore = (scores, keys, fallback = 0) => clampPercent(Number(getScore(scores, keys, fallback)), fallback);

const formatPercentScore = (value, fallback = 0) => `${Math.round(clampPercent(value, fallback))}%`;
const formatPlainScore = (value, fallback = 0) => Math.round(clampPercent(value, fallback));

const isLikelyImageSource = (value) => {
  if (!value) return false;
  const text = String(value);
  return /^(data:image|blob:)/i.test(text) ||
    /\/api\/persona\/storage\//i.test(text) ||
    /\/api\/persona\/share-links\/[^/]+\/assets\//i.test(text) ||
    /\.(png|jpe?g|gif|webp|avif|svg)(\?|#|$)/i.test(text);
};

const firstImageSource = (...values) => {
  for (const value of values) {
    if (value && isLikelyImageSource(value)) return value;
  }
  return '';
};

const isPersonaStorageImage = (value) => /\/api\/persona\/storage\//i.test(String(value || ''));

const getFigmaNodeUrl = (file, flow) => {
  const fileLink = firstText(file?.figma_file_link, file?.figmaFileLink);
  const fileKey = firstText(file?.figma_file_key, file?.figmaFileKey);
  const baseUrl = fileLink || (fileKey ? `https://www.figma.com/design/${fileKey}` : '');
  if (!baseUrl) return '';
  try {
    const url = new URL(baseUrl);
    const nodeId = firstText(flow?.figma_start_node_id, flow?.figmaStartNodeId, flow?.figma_node_id, flow?.figmaNodeId);
    if (nodeId) {
      url.searchParams.set('node-id', nodeId.replace(/:/g, '-'));
    }
    url.searchParams.set('hide-ui', '1');
    return url.toString();
  } catch {
    return '';
  }
};

const getFigmaEmbedUrl = (file, flow) => {
  const nodeUrl = getFigmaNodeUrl(file, flow);
  if (!nodeUrl) return '';
  return `https://www.figma.com/embed?embed_host=reopsai&url=${encodeURIComponent(nodeUrl)}`;
};

const normalizeFigmaEmbedUrl = (embedUrl) => {
  if (!embedUrl) return '';
  try {
    const url = new URL(embedUrl);
    const embeddedUrl = url.searchParams.get('url');
    if (!embeddedUrl) return embedUrl;
    const figmaUrl = new URL(embeddedUrl);
    figmaUrl.searchParams.set('hide-ui', '1');
    url.searchParams.set('url', figmaUrl.toString());
    return url.toString();
  } catch {
    return embedUrl;
  }
};

const getScreenFigmaEmbed = (screen, sourceData, fallbackScreen = null) => (
  normalizeFigmaEmbedUrl(
    firstText(
      screen?.figma_embed_url,
      screen?.figmaEmbedUrl,
      fallbackScreen?.figma_embed_url,
      fallbackScreen?.figmaEmbedUrl,
    ) || getFigmaEmbedUrl(
      screen?.figma_file || screen?.figmaFile || fallbackScreen?.figma_file || fallbackScreen?.figmaFile || sourceData?.figma_file || sourceData?.figmaFile,
      screen?.figma_flow || screen?.figmaFlow || fallbackScreen?.figma_flow || fallbackScreen?.figmaFlow || sourceData?.figma_flow || sourceData?.figmaFlow,
    )
  )
);

const getScreenImage = (screen, sourceData, fallbackScreen = null) => (
  firstImageSource(
    screen?.image_url,
    screen?.imageUrl,
    screen?.capturedImageUrl,
    screen?.captured_image_url,
    screen?.thumbnail_url,
    screen?.thumbnailUrl,
    screen?.source_image_url,
    screen?.sourceImageUrl,
    screen?.source,
    fallbackScreen?.image_url,
    fallbackScreen?.imageUrl,
    fallbackScreen?.capturedImageUrl,
    fallbackScreen?.captured_image_url,
    fallbackScreen?.thumbnail_url,
    fallbackScreen?.thumbnailUrl,
    fallbackScreen?.source_image_url,
    fallbackScreen?.sourceImageUrl,
    fallbackScreen?.source,
    sourceData?.image_url,
    sourceData?.imageUrl,
    sourceData?.capturedImageUrl,
    sourceData?.captured_image_url,
  )
);

const findMatchingSourceScreen = (screens, entry, index) => (
  screens.find((screen) => {
    const screenId = screen?.id || screen?.screenId || screen?.screen_id;
    const entryId = entry?.id || entry?.screenId || entry?.screen_id;
    return screenId && entryId && String(screenId) === String(entryId);
  }) || screens[index] || null
);

const getSourceScreens = (test) => {
  const sourceData = test?.source_data || test?.sourceData || {};
  const sourceScreens = asArray(sourceData.screens);
  const entries = [
    ...asArray(sourceData.imageEntries || sourceData.image_entries),
    ...asArray(sourceData.urlEntries || sourceData.url_entries),
    ...asArray(sourceData.figmaScreens || sourceData.figma_screens),
  ];
  const screens = entries.length ? entries : sourceScreens;
  if (screens.length) {
    return screens.map((screen, index) => {
      const fallbackScreen = entries.length ? findMatchingSourceScreen(sourceScreens, screen, index) : null;
      return {
        ...fallbackScreen,
        ...screen,
        id: screen.id || screen.screenId || fallbackScreen?.id || fallbackScreen?.screenId || `screen-${index + 1}`,
        label: screen.name || screen.fileName || screen.pageTitle || screen.url || fallbackScreen?.name || fallbackScreen?.label || `화면 ${index + 1}`,
        image_url: getScreenImage(screen, sourceData, fallbackScreen),
        figma_embed_url: getScreenFigmaEmbed(screen, sourceData, fallbackScreen),
        url: screen.url || fallbackScreen?.url,
      };
    });
  }
  const urls = asArray(sourceData.urls);
  if (urls.length) {
    return urls.map((item, index) => ({
      ...item,
      id: item.id || `screen-${index + 1}`,
      label: item.name || item.pageTitle || item.url || `화면 ${index + 1}`,
      image_url: getScreenImage(item, sourceData),
      figma_embed_url: getScreenFigmaEmbed(item, sourceData),
    }));
  }
  return [{
    id: 'screen-1',
    label: sourceData?.figma_flow?.figma_flow_name || sourceData?.figmaFlow?.figmaFlowName || '화면 1',
    image_url: getScreenImage(sourceData, sourceData),
    figma_embed_url: getScreenFigmaEmbed(sourceData, sourceData),
    url: sourceData.url,
  }];
};

function AuthenticatedScreenImage({ src, alt, fallbackText }) {
  const [objectUrl, setObjectUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const resolvedSrc = useMemo(() => personaAssetUrl(src), [src]);

  useEffect(() => {
    let cancelled = false;
    let nextObjectUrl = '';

    setFailed(false);
    setObjectUrl('');

    if (!src || !isPersonaStorageImage(src)) {
      return undefined;
    }

    apiFetch(src, { method: 'GET' })
      .then((response) => {
        if (!response.ok) throw new Error(`이미지 요청 실패: ${response.status}`);
        return response.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        nextObjectUrl = URL.createObjectURL(blob);
        setObjectUrl(nextObjectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (nextObjectUrl) URL.revokeObjectURL(nextObjectUrl);
    };
  }, [src]);

  const displaySrc = isPersonaStorageImage(src) ? objectUrl : resolvedSrc;
  if (!displaySrc || failed) {
    return (
      <div className="ui-result-screen-empty">
        <strong>화면 이미지를 불러오지 못했습니다</strong>
        <p>{fallbackText || '이미지 권한 또는 파일 상태를 확인해 주세요.'}</p>
      </div>
    );
  }

  return <img src={displaySrc} alt={alt || ''} onError={() => setFailed(true)} />;
}

const getScreenIndex = (value, screens) => {
  const raw = Number(value ?? 0);
  if (!Number.isFinite(raw)) return 0;
  if (raw >= 0 && raw < screens.length) return raw;
  if (raw > 0 && raw <= screens.length) return raw - 1;
  return 0;
};

const resolveScreenIndex = (item, screens) => {
  const screenId = item?.screenId || item?.screen_id;
  if (screenId) {
    const matchedIndex = screens.findIndex((screen) => String(screen.id) === String(screenId));
    if (matchedIndex >= 0) return matchedIndex;
  }
  return getScreenIndex(item?.screenIndex ?? item?.screen_index, screens);
};

const normalizeCommentTone = (type, fallbackTone = 'negative') => {
  if (['positive', 'strength', 'praise'].includes(type)) return 'positive';
  if (['negative', 'risk', 'issue', 'problem', 'improvement'].includes(type)) return 'negative';
  return fallbackTone;
};

const SCREEN_MARKER_POSITIONS = [
  { x: 16, y: 14 },
  { x: 44, y: 30 },
  { x: 63, y: 48 },
  { x: 28, y: 63 },
  { x: 74, y: 72 },
  { x: 79, y: 38 },
  { x: 67, y: 22 },
  { x: 12, y: 56 },
];

const fallbackMarkerPosition = (index) => SCREEN_MARKER_POSITIONS[index % SCREEN_MARKER_POSITIONS.length];

const clampPercent = (value, fallback = 50) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(Math.max(number, 0), 100);
};

const normalizeMarkerPercent = (value, fallback = 50) => {
  if (typeof value === 'boolean') return fallback;
  const rawValue = typeof value === 'string' ? value.trim().replace(/%$/, '') : value;
  const number = Number(rawValue);
  if (!Number.isFinite(number)) return fallback;
  const percent = number >= 0 && number <= 1 ? number * 100 : number;
  return Math.min(Math.max(percent, 0), 100);
};

const hasMarkerPercent = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'boolean') return false;
  const rawValue = typeof value === 'string' ? value.trim().replace(/%$/, '') : value;
  if (rawValue === '') return false;
  return Number.isFinite(Number(rawValue));
};

const markerNumberValue = (value) => {
  if (!hasMarkerPercent(value)) return null;
  const rawValue = typeof value === 'string' ? value.trim().replace(/%$/, '') : value;
  const number = Number(rawValue);
  return Number.isFinite(number) ? number : null;
};

const firstMarkerValue = (...values) => {
  for (const value of values) {
    if (hasMarkerPercent(value)) return value;
  }
  return undefined;
};

const markerPositionStyle = (comment) => ({
  left: `${normalizeMarkerPercent(comment.x)}%`,
  top: `${normalizeMarkerPercent(comment.y)}%`,
});

const isScrollableElement = (element) => Boolean(
  element && element.scrollHeight > element.clientHeight + 1
);

const isBackendFallbackMarkerPosition = (pin, index) => {
  if (pin?.hasMarkerCoordinates !== undefined || pin?.has_marker_coordinates !== undefined) return false;
  const x = normalizeMarkerPercent(pin?.x, -1);
  const y = normalizeMarkerPercent(pin?.y, -1);
  return x === 42 + (index % 3) * 8 && y === 38 + (index % 3) * 10;
};

const normalizeMatchText = (value) => String(value || '').trim().toLowerCase();
const normalizeMatchKey = (value) => normalizeMatchText(value).replace(/[^a-z0-9가-힣]/g, '');

const getPinTargetText = (pin) => normalizeMatchText(firstText(
  pin?.content,
  pin?.comment,
  pin?.targetElement,
  pin?.target_element,
  pin?.controlText,
  pin?.control_text,
  pin?.controlNodeName,
  pin?.control_node_name,
));

const getInteractionHints = (screen) => (
  asArray(screen?.interactionHints || screen?.interaction_hints)
    .filter((hint) => hint && hasMarkerPercent(hint.centerX ?? hint.center_x) && hasMarkerPercent(hint.centerY ?? hint.center_y))
);

const pinMatchesInteractionHint = (pin, hint) => {
  if (!pin || !hint) return false;
  const pinControlId = firstText(pin.controlNodeId, pin.control_node_id, pin.targetNodeId, pin.target_node_id);
  const hintControlId = firstText(hint.controlNodeId, hint.control_node_id);
  if (pinControlId && hintControlId && String(pinControlId) === String(hintControlId)) return true;

  const pinText = getPinTargetText(pin);
  if (!pinText) return false;
  return [
    hint.controlText,
    hint.control_text,
    hint.controlNodeName,
    hint.control_node_name,
  ].some((value) => {
    const hintText = normalizeMatchText(value);
    return hintText.length >= 2 && (pinText.includes(hintText) || hintText.includes(pinText));
  });
};

const pinMatchesOwnControlText = (pin) => {
  const pinText = getPinTargetText(pin);
  if (!pinText) return false;
  return [
    pin?.controlText,
    pin?.control_text,
    pin?.controlNodeName,
    pin?.control_node_name,
  ].some((value) => {
    const controlText = normalizeMatchText(value);
    return controlText.length >= 2 && (pinText.includes(controlText) || controlText.includes(pinText));
  });
};

const getPinCoordinate = (pin, axis) => {
  const position = pin?.position || pin?.marker || pin?.coordinates || pin?.coordinate || pin?.target || {};
  const point = pin?.point || pin?.center || {};
  const bounds = pin?.bounds || pin?.boundingBox || pin?.bounding_box || pin?.rect || {};
  if (axis === 'x') {
    const boundsX = markerNumberValue(bounds.x);
    const boundsWidth = markerNumberValue(bounds.width);
    const boundsCenterX = boundsX !== null && boundsWidth !== null
      ? boundsX + boundsWidth / 2
      : undefined;
    return firstMarkerValue(
      pin?.x,
      pin?.left,
      pin?.centerX,
      pin?.center_x,
      pin?.coordinateX,
      pin?.coordinate_x,
      position.x,
      position.left,
      position.centerX,
      position.center_x,
      point.x,
      point.centerX,
      bounds.centerX,
      bounds.center_x,
      boundsCenterX,
    );
  }
  const boundsY = markerNumberValue(bounds.y);
  const boundsHeight = markerNumberValue(bounds.height);
  const boundsCenterY = boundsY !== null && boundsHeight !== null
    ? boundsY + boundsHeight / 2
    : undefined;
  return firstMarkerValue(
    pin?.y,
    pin?.top,
    pin?.centerY,
    pin?.center_y,
    pin?.coordinateY,
    pin?.coordinate_y,
    position.y,
    position.top,
    position.centerY,
    position.center_y,
    point.y,
    point.centerY,
    bounds.centerY,
    bounds.center_y,
    boundsCenterY,
  );
};

const getPinMarkerData = ({ pin, screen, index, fallbackPosition }) => {
  const hints = getInteractionHints(screen);
  const matchedHint = hints.find((hint) => pinMatchesInteractionHint(pin, hint));
  if (matchedHint) {
    return {
      x: normalizeMarkerPercent(matchedHint.centerX ?? matchedHint.center_x, fallbackPosition.x),
      y: normalizeMarkerPercent(matchedHint.centerY ?? matchedHint.center_y, fallbackPosition.y),
      hasMarkerCoordinates: true,
    };
  }

  const coordinateSource = normalizeMatchKey(pin?.coordinateSource || pin?.coordinate_source);
  const explicitCoordinateFlag = pin.hasMarkerCoordinates ?? pin.has_marker_coordinates;
  const xValue = getPinCoordinate(pin, 'x');
  const yValue = getPinCoordinate(pin, 'y');
  const hasRawCoordinates = hasMarkerPercent(xValue) && hasMarkerPercent(yValue) && !isBackendFallbackMarkerPosition({ x: xValue, y: yValue }, index);
  const looksLikeUnmatchedControlHint = coordinateSource === 'figmacontrolbounds' && !pinMatchesOwnControlText(pin);
  const hasCoordinates = !looksLikeUnmatchedControlHint && (
    typeof explicitCoordinateFlag === 'boolean' ? explicitCoordinateFlag && hasRawCoordinates : hasRawCoordinates
  );

  return {
    x: normalizeMarkerPercent(xValue, fallbackPosition.x),
    y: normalizeMarkerPercent(yValue, fallbackPosition.y),
    hasMarkerCoordinates: hasCoordinates,
  };
};

const getResultPersonaId = (result, persona) => String(result?.persona_id ?? result?.personaId ?? persona?.id ?? '');
const getCommentAnchorId = (commentId) => `ui-comment-${String(commentId || '').replace(/[^a-zA-Z0-9_-]/g, '-')}`;

const getResultPinComments = (result) => asArray(result?.pinComments || result?.pin_comments);

const getResultScreenFeedbacks = (result) => {
  const feedback = result?.feedback || {};
  return asArray(feedback.screenFeedbacks || feedback.screen_feedbacks);
};

const getResultScreenSummaries = (result) => {
  const feedback = result?.feedback || {};
  return asArray(feedback.screenSummaries || feedback.screen_summaries);
};

const getScreenSummary = (result, screenIndex, screens) => {
  if (screenIndex == null || screenIndex < 0) return null;
  return getResultScreenSummaries(result).find(
    (item) => resolveScreenIndex(item, screens) === screenIndex,
  ) || null;
};

const getResultFlowAnalysis = (result) => asArray(result?.flowAnalysis || result?.flow_analysis);

const getPersonaSegmentLabel = (persona) => normalizePersonaTag(persona?.tag);

const getResultFlowAnalysisItem = (result, screens, screenIndex) => (
  getResultFlowAnalysis(result).find((item) => resolveScreenIndex(item, screens) === screenIndex) || null
);

const getResultScreenInsights = (result) => asArray(result?.screenInsights || result?.screen_insights);

const getResultScreenInsight = (result, screen, screenIndex) => {
  const insights = getResultScreenInsights(result);
  return insights.find((item) => String(item.screenId || item.screen_id) === String(screen?.id)) || insights[screenIndex] || null;
};

const getFlowRiskScore = (flowItem) => (
  clampPercent(flowItem?.confusionScore ?? flowItem?.confusion_score, 0) +
  clampPercent(flowItem?.dropoffRisk ?? flowItem?.dropoff_risk, 0)
);

const getRiskMetricClass = (score) => {
  const value = clampPercent(score, 0);
  if (value >= 70) return 'metric-red';
  if (value >= 40) return 'metric-orange';
  return 'metric-green';
};

const getCompletionMetricClass = (score) => {
  const value = clampPercent(score, 0);
  if (value >= 70) return 'metric-green';
  if (value >= 40) return 'metric-orange';
  return 'metric-red';
};

const getSingleScreenMetricClass = (score) => {
  const value = clampPercent(score, 0);
  if (value >= 80) return 'metric-green';
  if (value >= 60) return 'metric-orange';
  return 'metric-red';
};

const buildResultComments = (results, screens, personas) => {
  const flattened = [];

  screens.forEach((screen, screenIndex) => {
    results.forEach((result) => {
      const persona = getResultPersona(result, personas);
      const personaId = getResultPersonaId(result, persona);
      const pins = getResultPinComments(result).filter((pin) => resolveScreenIndex(pin, screens) === screenIndex);
      const appendInsightFallbacks = () => {
        const scoped = flattened.filter((comment) => comment.personaId === personaId && comment.screenIndex === screenIndex);
        const insight = getResultScreenInsight(result, screen, screenIndex);
        const positiveText = firstText(
          asArray(insight?.positives)[0],
          screenIndex === 0 ? asArray(result.strengths)[0] : '',
        );
        const negativeText = firstText(
          asArray(insight?.issues)[0],
          screenIndex === 0 ? asArray(result.risks)[0] : '',
        );

        if (positiveText && !scoped.some((comment) => comment.type === 'positive')) {
          flattened.push({
            id: `${result.id}-insight-positive-${screenIndex}`,
            persona,
            personaId,
            screenIndex,
            screenId: screen.id,
            type: 'positive',
            pinType: 'praise',
            content: positiveText,
            hasMarkerCoordinates: false,
            ...fallbackMarkerPosition(flattened.length),
          });
        }

        if (negativeText && !scoped.some((comment) => comment.type === 'negative')) {
          flattened.push({
            id: `${result.id}-insight-negative-${screenIndex}`,
            persona,
            personaId,
            screenIndex,
            screenId: screen.id,
            type: 'negative',
            pinType: 'improvement',
            content: negativeText,
            hasMarkerCoordinates: false,
            ...fallbackMarkerPosition(flattened.length),
          });
        }
      };

      if (pins.length > 0) {
        pins.forEach((pin, index) => {
          const fallbackPosition = fallbackMarkerPosition(flattened.length);
          const marker = getPinMarkerData({ pin, screen, index, fallbackPosition });
          flattened.push({
            id: `${result.id}-pin-${screenIndex}-${index}`,
            persona,
            personaId,
            screenIndex,
            screenId: screen.id,
            type: normalizeCommentTone(pin.type),
            pinType: pin.type || null,
            source: pin.source || null,
            content: pin.content || pin.comment || getFeedbackSummary(result),
            x: marker.x,
            y: marker.y,
            hasMarkerCoordinates: marker.hasMarkerCoordinates,
          });
        });
        appendInsightFallbacks();
        return;
      }

      getResultScreenFeedbacks(result)
        .filter((item) => resolveScreenIndex(item, screens) === screenIndex)
        .forEach((item, index) => {
          const scores = result.scores || {};
          const fallbackTone = Number(scores.overall ?? scores.usability ?? 0) >= 70 ? 'positive' : 'negative';
          const fallbackPosition = fallbackMarkerPosition(flattened.length);
          flattened.push({
            id: `${result.id}-feedback-${screenIndex}-${index}`,
            persona,
            personaId,
            screenIndex,
            screenId: screen.id,
            type: normalizeCommentTone(item.type, fallbackTone),
            pinType: null,
            content: item.feedback || item.content || item.comment || getFeedbackSummary(result),
            hasMarkerCoordinates: false,
            x: fallbackPosition.x,
            y: fallbackPosition.y,
          });
        });
      appendInsightFallbacks();
    });
  });

  const toneCounters = {};
  return flattened.map((comment) => {
    const counterKey = `${comment.screenIndex}:${comment.type}`;
    toneCounters[counterKey] = (toneCounters[counterKey] || 0) + 1;
    const markerNumber = toneCounters[counterKey];
    return {
      ...comment,
      markerNumber,
      label: `${comment.type === 'positive' ? '긍정' : '부정'} ${markerNumber}`,
    };
  });
};

const getPersonaCommentCounts = (result, screen, screenIndex, comments) => {
  const insight = getResultScreenInsight(result, screen, screenIndex);
  if (insight) {
    const positive = asArray(insight.positives).length;
    const negative = asArray(insight.issues).length;
    if (positive > 0 || negative > 0) return { positive, negative };
  }

  const persona = result?.personaSnapshot || result?.persona_snapshot || {};
  const personaId = String(result?.persona_id ?? result?.personaId ?? persona.id ?? '');
  const scoped = comments.filter((comment) => (
    comment.personaId === personaId &&
    comment.screenIndex === screenIndex
  ));

  return {
    positive: scoped.filter((comment) => comment.type === 'positive').length,
    negative: scoped.filter((comment) => comment.type === 'negative').length,
  };
};

const getFlowRiskStage = (result, screens, fallbackIndex = 0) => {
  const flowItems = getResultFlowAnalysis(result);
  const explicitStage = flowItems.find((item) => (
    item?.riskStage || item?.risk_stage || item?.expectedRiskStage || item?.expected_risk_stage
  ));
  if (explicitStage) {
    return explicitStage.riskStage || explicitStage.risk_stage || explicitStage.expectedRiskStage || explicitStage.expected_risk_stage;
  }

  const riskiest = flowItems.reduce((current, item) => {
    const score = getFlowRiskScore(item);
    if (!current || score > current.score) {
      return { item, score };
    }
    return current;
  }, null);
  const screenIndex = riskiest?.item ? resolveScreenIndex(riskiest.item, screens) : fallbackIndex;
  return `Flow ${screenIndex + 1}`;
};

const getRiskiestFlowScreenIndex = (results, screens, comments = []) => {
  if (!Array.isArray(screens) || screens.length === 0) return null;

  const riskScores = new Map();
  asArray(results).forEach((result) => {
    getResultFlowAnalysis(result).forEach((item) => {
      const screenIndex = resolveScreenIndex(item, screens);
      if (screenIndex < 0 || screenIndex >= screens.length) return;
      riskScores.set(screenIndex, (riskScores.get(screenIndex) || 0) + getFlowRiskScore(item));
    });
  });

  if (riskScores.size > 0) {
    let riskiestIndex = null;
    let highestRisk = Number.NEGATIVE_INFINITY;
    riskScores.forEach((score, screenIndex) => {
      if (score > highestRisk) {
        highestRisk = score;
        riskiestIndex = screenIndex;
      }
    });
    return riskiestIndex;
  }

  const negativeCounts = screens.map((_, screenIndex) => (
    comments.filter((comment) => comment.screenIndex === screenIndex && comment.type === 'negative').length
  ));
  const highestCount = Math.max(...negativeCounts);
  return highestCount > 0 ? negativeCounts.indexOf(highestCount) : null;
};

function UITestPageShell({ isShareView, children }) {
  if (isShareView) {
    return <>{children}</>;
  }

  return (
    <B2BLayout hideCollapseButton={true} defaultCollapsed={true} hidePageHeader={true}>
      {children}
    </B2BLayout>
  );
}

const getResultOverallFlowScore = (result) => {
  const scores = result?.scores || {};
  const value = scores.overallFlowScore ?? scores.overall_flow_score ?? scores.overall;
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return clampPercent(value, null);
};

const deriveRawFlowCompletionRate = (result) => getResultOverallFlowScore(result);

const FLOW_STEP_AVERAGE_WEIGHT = 0.45;
const FLOW_STEP_MAX_WEIGHT = 0.45;
const FLOW_STEP_COVERAGE_WEIGHT = 0.1;

const aggregateFlowStepScores = (scores) => {
  const values = scores.filter((score) => Number.isFinite(score));
  if (!values.length) return 0;
  const averageScore = values.reduce((sum, score) => sum + score, 0) / values.length;
  const maxScore = Math.max(...values);
  const coverageScore = (values.filter((score) => score > 0).length / values.length) * 100;
  return clampPercent(
    averageScore * FLOW_STEP_AVERAGE_WEIGHT +
      maxScore * FLOW_STEP_MAX_WEIGHT +
      coverageScore * FLOW_STEP_COVERAGE_WEIGHT,
    0,
  );
};

const capCompletionRateByFlowRisk = (completionRate, confusionScore, dropoutRiskScore) => {
  const flowFailureRisk = confusionScore * 0.45 + dropoutRiskScore * 0.55;
  const riskCeiling = Math.max(0, Math.min(flowFailureRisk > 0 ? 99 : 100, 100 - flowFailureRisk * 0.65));
  return Math.round(Math.min(completionRate, riskCeiling));
};

const deriveFlowPersonaMetrics = (result) => {
  const flowAnalysis = getResultFlowAnalysis(result);
  const scores = result?.scores || {};
  const rawCompletionRate = deriveRawFlowCompletionRate(result);

  if (!flowAnalysis.length) {
    const confusionScore = clampPercent(100 - getNumericScore(scores, ['clarity', 'clear', 'readability'], 70), 35);
    const dropoutRiskScore = clampPercent(100 - getNumericScore(scores, ['usability', 'ease'], 70), 30);
    return {
      confusionScore,
      dropoutRiskScore,
      completionRateScore: rawCompletionRate ?? 0,
    };
  }

  const confusionScore = aggregateFlowStepScores(
    flowAnalysis.map((item) => clampPercent(item?.confusionScore ?? item?.confusion_score, 0)),
  );
  const dropoutRiskScore = aggregateFlowStepScores(
    flowAnalysis.map((item) => clampPercent(item?.dropoffRisk ?? item?.dropoff_risk, 0)),
  );
  return {
    confusionScore,
    dropoutRiskScore,
    completionRateScore: rawCompletionRate !== null
      ? capCompletionRateByFlowRisk(rawCompletionRate, confusionScore, dropoutRiskScore)
      : null,
  };
};

const getScreenScoreEntry = (result, screen, screenIndex, screens) => {
  const entries = asArray(
    result?.scores?.screenScores ||
    result?.scores?.screen_scores ||
    result?.screenScores ||
    result?.screen_scores
  );
  return entries.find((item) => {
    const itemScreenId = item?.screenId || item?.screen_id;
    if (itemScreenId && screen?.id && String(itemScreenId) === String(screen.id)) return true;
    return resolveScreenIndex(item, screens) === screenIndex;
  }) || entries[screenIndex] || null;
};

const getPersonaScoresForScreen = (result, screen, screenIndex, screens) => {
  const globalScores = result?.scores || {};
  const entry = getScreenScoreEntry(result, screen, screenIndex, screens);
  const fallbackClarity = getNumericScore(globalScores, ['clarity', 'clear', 'readability'], 70);
  const fallbackUsability = getNumericScore(globalScores, ['usability', 'ease'], 70);
  const fallbackSatisfaction = getNumericScore(globalScores, ['appeal', 'satisfaction', 'score'], 65);

  if (entry) {
    return {
      clarity: getNumericScore(entry, ['clarity', 'clear', 'readability'], fallbackClarity),
      usability: getNumericScore(entry, ['usability', 'ease'], fallbackUsability),
      satisfaction: getNumericScore(entry, ['appeal', 'satisfaction', 'score'], fallbackSatisfaction),
    };
  }

  const flowItem = getResultFlowAnalysis(result)
    .find((item) => resolveScreenIndex(item, screens) === screenIndex);
  if (flowItem) {
    const clarity = 100 - clampPercent(flowItem.confusionScore ?? flowItem.confusion_score, 100 - fallbackClarity);
    const usability = 100 - clampPercent(flowItem.dropoffRisk ?? flowItem.dropoff_risk, 100 - fallbackUsability);
    const satisfaction = fallbackSatisfaction;
    return { clarity, usability, satisfaction };
  }

  return {
    clarity: fallbackClarity,
    usability: fallbackUsability,
    satisfaction: fallbackSatisfaction,
  };
};

const getPersonaFeedbackSummaryForScreen = (result, screenIndex, screens, isFlowResult) => {
  if (isFlowResult) {
    return getFeedbackSummary(result);
  }

  const screenSummary = getScreenSummary(result, screenIndex, screens);
  const summaryText = firstText(screenSummary?.summary);
  if (summaryText) return summaryText;

  const feedback = result?.feedback || {};
  const overallText = firstText(feedback.overallFeedback, feedback.overall_feedback);
  if (overallText) return overallText;

  if (screenIndex >= 0) {
    const screenFeedback = getResultScreenFeedbacks(result).find(
      (item) => resolveScreenIndex(item, screens) === screenIndex,
    );
    const screenText = firstText(screenFeedback?.feedback, screenFeedback?.content, screenFeedback?.comment);
    if (screenText) return screenText;
  }

  return getFeedbackSummary(result);
};

const UI_TEST_MAX_MATRIX_COLUMNS = 12;
const UI_TEST_MATRIX_META_HEADERS = [
  '항목',
  '내용',
  ...Array.from({ length: UI_TEST_MAX_MATRIX_COLUMNS - 2 }, () => ''),
];

const UI_TEST_COMMENT_HEADERS = [
  '화면/단계',
  '퍼소나명',
  '퍼소나 태그',
  '코멘트',
  '내용',
];

const getPersonaTagText = (persona = {}) => (
  persona.tag ||
  persona.personaTag ||
  persona.persona_tag ||
  getPersonaSegmentLabel(persona) ||
  ''
);

const getPersonaInfoText = (persona = {}) => [
  persona.gender,
  persona.age,
  persona.role || persona.title || persona.occupation,
].filter(Boolean).join(' / ');

const getTestTypeText = (test, isFlowResult) => [
  test?.validation_type === 'ab' ? 'A/B 비교 검증' : '화면 사용성 검증',
  isFlowResult ? '플로우' : '단일 화면',
  test?.source_type === 'figma' ? 'Figma' : test?.source_type === 'url' ? 'URL' : test?.source_type === 'image' ? 'Image' : test?.source_type,
].filter(Boolean).join(' / ');

const getExportScreenKey = (screenIndex, isFlowResult) => `${isFlowResult ? 'Flow' : '화면'} ${screenIndex + 1}`;

const getExportScreenLabel = (screen, screenIndex, isFlowResult) => (
  isFlowResult ? `Flow ${screenIndex + 1}` : (screen.label || screen.name || `화면 ${screenIndex + 1}`)
);

const toMatrixRow = (width, ...values) => {
  const row = values.slice(0, width);
  while (row.length < width) row.push('');
  return row;
};

const getUITestMatrixHeaders = (isFlowResult) => (
  isFlowResult
    ? [
        '퍼소나명',
        '퍼소나 태그',
        '퍼소나 정보',
        '생성여부',
        '전체/단계명',
        '구분(종합/개별)',
        '혼란도',
        '이탈위험',
        '완료가능성',
        '예상위험단계',
        '값',
        '코멘트',
      ]
    : [
        '퍼소나명',
        '퍼소나 태그',
        '퍼소나 정보',
        '생성여부',
        '화면명',
        '구분(종합/개별)',
        '값',
        '코멘트',
      ]
);

const getUITestColumnWidths = (isFlowResult) => (
  isFlowResult
    ? [18, 14, 28, 14, 16, 18, 12, 12, 12, 16, 18, 60]
    : [18, 18, 28, 14, 14, 18, 34, 60]
);

const buildUITestExportData = ({
  test,
  results,
  screens,
  personas,
  isFlowResult,
  comments,
  includeResultComments = true,
}) => {
  const detailRows = [];
  const commentRows = [];
  const matrixHeaders = getUITestMatrixHeaders(isFlowResult);
  const matrixWidth = matrixHeaders.length;
  const matrixMetaHeaders = UI_TEST_MATRIX_META_HEADERS.slice(0, matrixWidth);
  const metaMatrixRows = [
    toMatrixRow(matrixWidth, '테스트명', test?.name || ''),
    toMatrixRow(matrixWidth, '테스트 유형', getTestTypeText(test, isFlowResult)),
    toMatrixRow(matrixWidth, '상태', test?.status || ''),
    toMatrixRow(matrixWidth, '생성일', formatResultDate(test?.created_at)),
    toMatrixRow(matrixWidth, '퍼소나 수', `${test?.persona_count || results.length || 0}명`),
    toMatrixRow(matrixWidth, '설명', test?.description || ''),
    ...screens.flatMap((screen, screenIndex) => {
      const screenKey = getExportScreenKey(screenIndex, isFlowResult);
      const screenLabel = getExportScreenLabel(screen, screenIndex, isFlowResult);
      const screenUrl = screen.url || screen.figma_node_url || screen.figmaNodeUrl || screen.figma_embed_url || screen.figmaEmbedUrl || '';
      return [
        toMatrixRow(matrixWidth, `${screenKey} 이름`, screenLabel),
        ...(screenUrl ? [toMatrixRow(matrixWidth, `${screenKey} URL`, screenUrl)] : []),
      ];
    }),
  ];
  results.forEach((result) => {
    const persona = getResultPersona(result, personas);
    const personaId = getResultPersonaId(result, persona);
    const flowMetrics = isFlowResult ? deriveFlowPersonaMetrics(result) : null;
    const personaName = persona.name || '';
    const personaTag = getPersonaTagText(persona);
    const personaInfo = getPersonaInfoText(persona);
    const status = result.status || '';

    if (isFlowResult) {
      detailRows.push(toMatrixRow(
        matrixWidth,
        personaName,
        personaTag,
        personaInfo,
        status,
        '플로우 전체',
        '종합 피드백',
        Math.round(clampPercent(flowMetrics?.confusionScore, 0)),
        Math.round(clampPercent(flowMetrics?.dropoutRiskScore, 0)),
        flowMetrics?.completionRateScore !== null && flowMetrics?.completionRateScore !== undefined
          ? Math.round(clampPercent(flowMetrics.completionRateScore, 0))
          : '',
        getFlowRiskStage(result, screens, 0),
        '',
        getFeedbackSummary(result),
      ));
    }

    const commentCounts = { positive: 0, negative: 0 };
    screens.forEach((screen, screenIndex) => {
      const scores = getPersonaScoresForScreen(result, screen, screenIndex, screens);
      const scopedComments = comments.filter((comment) => (
        comment.personaId === personaId &&
        comment.screenIndex === screenIndex
      ));
      const positiveComments = scopedComments.filter((comment) => comment.type === 'positive');
      const negativeComments = scopedComments.filter((comment) => comment.type !== 'positive');
      const screenKey = getExportScreenKey(screenIndex, isFlowResult);
      const screenLabel = getExportScreenLabel(screen, screenIndex, isFlowResult);

      if (!isFlowResult) {
        detailRows.push(toMatrixRow(
          matrixWidth,
          personaName,
          personaTag,
          personaInfo,
          status,
          screenKey,
          '종합 피드백',
          `명확성 ${Math.round(clampPercent(scores.clarity, 0))}% / 사용성 ${Math.round(clampPercent(scores.usability, 0))}% / 만족도 ${Math.round(clampPercent(scores.satisfaction, 0))}%`,
          getPersonaFeedbackSummaryForScreen(result, screenIndex, screens, isFlowResult),
        ));
      }

      positiveComments.forEach((comment) => {
        commentCounts.positive += 1;
        const commentNumber = `긍정 코멘트${commentCounts.positive}`;
        if (includeResultComments) {
          detailRows.push(toMatrixRow(
            matrixWidth,
            personaName,
            personaTag,
            personaInfo,
            status,
            screenKey,
            '개별 피드백',
            ...(isFlowResult ? ['', '', '', '', commentNumber] : [commentNumber]),
            comment.content || '',
          ));
        }
        commentRows.push({
          '화면/단계': screenLabel,
          '퍼소나명': personaName,
          '퍼소나 태그': personaTag,
          '코멘트': commentNumber,
          '내용': comment.content || '',
        });
      });
      negativeComments.forEach((comment) => {
        commentCounts.negative += 1;
        const commentNumber = `부정 코멘트${commentCounts.negative}`;
        if (includeResultComments) {
          detailRows.push(toMatrixRow(
            matrixWidth,
            personaName,
            personaTag,
            personaInfo,
            status,
            screenKey,
            '개별 피드백',
            ...(isFlowResult ? ['', '', '', '', commentNumber] : [commentNumber]),
            comment.content || '',
          ));
        }
        commentRows.push({
          '화면/단계': screenLabel,
          '퍼소나명': personaName,
          '퍼소나 태그': personaTag,
          '코멘트': commentNumber,
          '내용': comment.content || '',
        });
      });
    });
  });
  const matrix = [
    matrixMetaHeaders,
    ...metaMatrixRows,
    toMatrixRow(matrixWidth),
    matrixHeaders,
    ...detailRows,
  ];

  return {
    matrix,
    sheets: [
      { name: '화면검증 결과', matrix, columnWidths: getUITestColumnWidths(isFlowResult) },
      { name: '코멘트 상세', headers: UI_TEST_COMMENT_HEADERS, rows: commentRows },
    ],
  };
};

function ModalFrame({ title, description, children, footer, onClose, className = '' }) {
  return (
    <div className="ui-result-modal-backdrop" role="presentation">
      <section
        className={`ui-result-modal${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ui-result-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <h2 id="ui-result-modal-title">{title}</h2>
          {description ? <p>{description}</p> : null}
        </header>
        <div className="ui-result-modal__body">{children}</div>
        <footer>{footer}</footer>
      </section>
    </div>
  );
}

function DeleteTestModal({ deleting, onCancel, onConfirm }) {
  return (
    <div className="persona-modal-backdrop" role="presentation">
      <section
        className="persona-delete-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ui-test-delete-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <h2 id="ui-test-delete-modal-title">선택한 테스트를 삭제하시겠습니까?</h2>
        </header>
        <div className="persona-delete-modal__body">
          <p>삭제 시 관련 해당 테스트의 정보는 모두 삭제되며</p>
          <p>이 작업은 취소할 수 없습니다.</p>
        </div>
        <footer>
          <button type="button" onClick={onCancel} disabled={deleting}>
            취소하기
          </button>
          <button type="button" onClick={onConfirm} disabled={deleting}>
            {deleting ? '삭제 중' : '삭제하기'}
          </button>
        </footer>
      </section>
    </div>
  );
}

function PersonaInfoRows({ title, rows }) {
  const displayRows = asArray(rows).filter(([, value]) => asDetailText(value, '') !== '');
  return (
    <section className="ui-result-persona-info-card">
      <h3>{title}</h3>
      <dl>
        {displayRows.length ? displayRows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{asDetailText(value)}</dd>
          </div>
        )) : (
          <div>
            <dt>정보</dt>
            <dd>-</dd>
          </div>
        )}
      </dl>
    </section>
  );
}

function PersonaInfoModal({ persona, onClose }) {
  const detail = buildPersonaDetail(persona) || {};
  const basicRows = asArray(detail.basic).filter(([label]) => ['나이', '세대', '성별'].includes(label));
  return (
    <ModalFrame
      title="퍼소나 기본정보"
      onClose={onClose}
      className="ui-result-modal--persona-info"
      footer={<button type="button" onClick={onClose}>확인</button>}
    >
      <div className="ui-result-persona-info">
        <div className="ui-result-persona-info__profile">
          <PersonaAvatar persona={persona} size="md" />
          <div>
            <strong>{persona?.name || '퍼소나'}</strong>
            {getPersonaSegmentLabel(persona) ? <PersonaTagChip tag={persona.tag} /> : null}
          </div>
        </div>
        <div className="ui-result-persona-info__grid">
          <PersonaInfoRows title="기본 정보" rows={basicRows} />
          <PersonaInfoRows title={detail.roleSectionTitle || '직업 정보'} rows={detail.job} />
        </div>
      </div>
    </ModalFrame>
  );
}

function UITestDetailPage({ shareMode = false }) {
  const navigate = useNavigate();
  const { testId, shareToken } = useParams();
  const isShareView = shareMode || Boolean(shareToken);
  const [test, setTest] = useState(null);
  const [results, setResults] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeScreenIndex, setActiveScreenIndex] = useState(0);
  const [commentFilter, setCommentFilter] = useState('all');
  const [personaFilter, setPersonaFilter] = useState('all');
  const [modal, setModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedPersonaInfo, setSelectedPersonaInfo] = useState(null);
  const [exportFormat, setExportFormat] = useState('csv');
  const [shareLink, setShareLink] = useState('');
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'success' });
  const [selectedCommentId, setSelectedCommentId] = useState('');
  const [feedbackPage, setFeedbackPage] = useState(0);
  const commentCardRefs = useRef({});
  const commentsScrollRef = useRef(null);
  const evaluationSectionRef = useRef(null);
  const evaluationHeadRef = useRef(null);
  const evaluationFlowHeadRef = useRef(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (isShareView) {
        const shared = await personaResultShareApi.getShare(shareToken);
        if (shared?.resourceType !== 'ui_test') throw new Error('공유 링크를 찾을 수 없습니다.');
        const sharedTest = shared.result || {};
        setTest(sharedTest);
        setResults(asArray(sharedTest.results));
        setPersonas([]);
        return;
      }
      const [testData, resultData, personaData] = await Promise.all([
        personaTestApi.getTest(testId),
        personaTestApi.listResults(testId),
        personaApi.listPersonas({ limit: 100 }),
      ]);
      setTest(testData);
      setResults(resultData);
      setPersonas(personaData.items || []);
    } catch (err) {
      setError(isShareView ? '공유 링크를 찾을 수 없습니다.' : (err.message || '테스트 정보를 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [isShareView, shareToken, testId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const isFlowResult = test?.scope_type === 'flow' && test?.validation_type !== 'ab';
  const screens = useMemo(() => getSourceScreens(test), [test]);
  const activeScreen = useMemo(() => screens[activeScreenIndex] || screens[0] || {}, [activeScreenIndex, screens]);
  const shareUrl = shareLink || (typeof window !== 'undefined' ? window.location.href : '');
  const allComments = useMemo(() => buildResultComments(results, screens, personas), [personas, results, screens]);
  const canOpenInsightReport = !isShareView && (test?.status === 'completed' || results.length > 0);

  const handleOpenInsightReport = () => {
    if (!canOpenInsightReport) return;
    const params = new URLSearchParams();
    if (!isFlowResult) {
      params.set('scopeIndex', String(activeScreenIndex));
    }
    const query = params.toString();
    navigate(`/tests/ui/${testId}/insights${query ? `?${query}` : ''}`);
  };
  const riskiestFlowScreenIndex = useMemo(() => (
    isFlowResult ? getRiskiestFlowScreenIndex(results, screens, allComments) : null
  ), [allComments, isFlowResult, results, screens]);
  const personaFeedbacks = useMemo(() => (
    results.map((result) => {
      const persona = getResultPersona(result, personas);
      const scores = getPersonaScoresForScreen(result, activeScreen, activeScreenIndex, screens);
      const commentCounts = getPersonaCommentCounts(result, activeScreen, activeScreenIndex, allComments);
      const flowMetrics = isFlowResult ? deriveFlowPersonaMetrics(result) : null;
      return {
        id: result.id,
        result,
        persona,
        summary: getPersonaFeedbackSummaryForScreen(result, activeScreenIndex, screens, isFlowResult),
        clarity: scores.clarity,
        usability: scores.usability,
        satisfaction: scores.satisfaction,
        confusion: flowMetrics?.confusionScore ?? 0,
        dropoffRisk: flowMetrics?.dropoutRiskScore ?? 0,
        completionRate: flowMetrics?.completionRateScore ?? 0,
        riskStage: getFlowRiskStage(result, screens, activeScreenIndex),
        positiveCount: commentCounts.positive,
        negativeCount: commentCounts.negative,
      };
    })
  ), [activeScreen, activeScreenIndex, allComments, isFlowResult, personas, results, screens]);
  const feedbackPageCount = Math.max(1, Math.ceil(personaFeedbacks.length / FEEDBACK_PAGE_SIZE));
  const normalizedFeedbackPage = Math.min(feedbackPage, feedbackPageCount - 1);
  const visiblePersonaFeedbacks = personaFeedbacks.slice(
    normalizedFeedbackPage * FEEDBACK_PAGE_SIZE,
    normalizedFeedbackPage * FEEDBACK_PAGE_SIZE + FEEDBACK_PAGE_SIZE
  );

  useEffect(() => {
    setFeedbackPage(0);
  }, [activeScreenIndex]);

  useEffect(() => {
    if (feedbackPage > feedbackPageCount - 1) {
      setFeedbackPage(Math.max(0, feedbackPageCount - 1));
    }
  }, [feedbackPage, feedbackPageCount]);

  const visibleComments = allComments.filter((comment) => {
    if (comment.screenIndex !== activeScreenIndex) return false;
    if (personaFilter !== 'all' && comment.personaId !== personaFilter) return false;
    if (commentFilter === 'positive') return comment.type === 'positive';
    if (commentFilter === 'negative') return comment.type !== 'positive';
    return true;
  });
  const displayComments = visibleComments;
  const screenMarkerComments = displayComments.filter((comment) => comment.hasMarkerCoordinates !== false);

  useEffect(() => {
    if (selectedCommentId && !displayComments.some((comment) => comment.id === selectedCommentId)) {
      setSelectedCommentId('');
    }
  }, [displayComments, selectedCommentId]);

  useEffect(() => {
    const section = evaluationSectionRef.current;
    const head = isFlowResult ? evaluationFlowHeadRef.current : evaluationHeadRef.current;
    if (!section || !head) return undefined;

    const syncHeadHeight = () => {
      section.style.setProperty('--ui-result-evaluation-head-height', `${head.offsetHeight}px`);
    };

    syncHeadHeight();
    const observer = new ResizeObserver(syncHeadHeight);
    observer.observe(head);
    return () => observer.disconnect();
  }, [isFlowResult, commentFilter, personaFilter, personaFeedbacks.length, screens.length]);

  const handleSelectCommentMarker = (commentId, event) => {
    event?.preventDefault();
    const pageScrollContainer = event?.currentTarget?.closest?.('.b2b-body') || null;
    const pageScrollTop = pageScrollContainer?.scrollTop ?? null;
    const windowScrollX = window.scrollX || window.pageXOffset || 0;
    const windowScrollY = window.scrollY || window.pageYOffset || 0;
    const shouldMoveFocus = event?.detail === 0;

    const restorePageScroll = () => {
      if (pageScrollContainer && pageScrollTop !== null && pageScrollContainer.scrollTop !== pageScrollTop) {
        pageScrollContainer.scrollTop = pageScrollTop;
      }
      if ((window.scrollX || window.pageXOffset || 0) !== windowScrollX || (window.scrollY || window.pageYOffset || 0) !== windowScrollY) {
        window.scrollTo(windowScrollX, windowScrollY);
      }
    };

    setSelectedCommentId(commentId);
    window.requestAnimationFrame(() => {
      const target = commentCardRefs.current[commentId] || document.getElementById(getCommentAnchorId(commentId));
      const scrollContainer = commentsScrollRef.current;
      if (target && scrollContainer && isScrollableElement(scrollContainer)) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const nextTop = scrollContainer.scrollTop + (targetRect.top - containerRect.top) - 8;
        scrollContainer.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
      }
      if (shouldMoveFocus) {
        target?.focus({ preventScroll: true });
      } else {
        restorePageScroll();
        window.requestAnimationFrame(restorePageScroll);
      }
    });
  };

  const handleCopyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setToast({ isOpen: true, message: '공유 링크가 복사되었습니다.', type: 'success' });
    } catch (err) {
      setError('공유 링크 복사에 실패했습니다.');
    }
  };

  const handleOpenShare = async () => {
    try {
      const share = await personaResultShareApi.createShare('ui_test', testId);
      setShareLink(share.shareUrl || '');
      setModal('share');
    } catch (err) {
      setError(err.message || '공유 링크를 생성하지 못했습니다.');
    }
  };

  const handleExport = () => {
    const exportData = buildUITestExportData({
      test,
      results,
      screens,
      personas,
      isFlowResult,
      comments: allComments,
      includeResultComments: exportFormat !== 'xlsx',
    });
    downloadExportFile({
      filename: sanitizeCsvFilename(test?.name, 'ui-test-result'),
      format: exportFormat,
      ...exportData,
    });
    setModal(null);
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await personaTestApi.deleteTest(testId);
      navigate('/tests', {
        state: { toast: { message: '테스트를 삭제했습니다.', type: 'success' } },
      });
    } catch (err) {
      setError(err.message || '테스트 삭제에 실패했습니다.');
      setDeleting(false);
    }
  };

  const handleOpenPersonaInfo = (persona) => {
    setSelectedPersonaInfo(persona);
    setModal('persona-info');
  };

  const handleClosePersonaInfo = () => {
    setModal(null);
    setSelectedPersonaInfo(null);
  };

  const pageClassName = `ui-result-page${isShareView ? ' share-view' : ''}`;

  if (loading) {
    return (
      <UITestPageShell isShareView={isShareView}>
        <main className={pageClassName}>
          <PersonaState type="loading" title="불러오는 중" message="테스트 결과를 가져오고 있습니다." />
        </main>
      </UITestPageShell>
    );
  }

  if (!test) {
    return (
      <UITestPageShell isShareView={isShareView}>
        <main className={pageClassName}>
          <ErrorBanner message={error} />
          <PersonaState
            title={isShareView ? '공유 링크를 찾을 수 없습니다' : '테스트를 찾을 수 없습니다'}
            action={isShareView ? null : <button type="button" className="persona-secondary-button" onClick={() => navigate('/tests')}>목록으로</button>}
          />
        </main>
      </UITestPageShell>
    );
  }

  return (
    <UITestPageShell isShareView={isShareView}>
      <main className={pageClassName}>
        {!isShareView ? (
          <PersonaResultHeader title={isFlowResult ? '사용자 플로우 검증 결과' : '화면 사용성 검증 결과'} onBack={() => navigate('/tests')} />
        ) : null}

        <section className="ui-result-body">
          <div className="ui-result-content">
            <ErrorBanner message={error} />
            <div className="ui-result-summary">
              <div>
                <h2>{test.name}</h2>
                <div className="ui-result-meta">
                  <span>{test.validation_type === 'ab' ? 'A/B비교 검증' : '화면 사용성 검증'}</span>
                  <ChevronRight size={16} />
                  <span>{test.scope_type === 'flow' ? '플로우' : '단일 화면'}</span>
                  <ChevronRight size={16} />
                  <span>{test.source_type === 'figma' ? 'Figma' : test.source_type === 'url' ? 'URL' : 'Image'}</span>
                  <i />
                  <UserRound size={16} />
                  <em>{test.persona_count || results.length || personaFeedbacks.length}명</em>
                  <i />
                  <em>{formatResultDate(test.created_at)}</em>
                  <span className={`ui-result-status ui-result-status--${test.status}`}>{statusLabel(test.status)}</span>
                </div>
              </div>
              {!isShareView ? (
                <div className="ui-result-actions">
                  <button type="button" onClick={handleOpenShare}><LinkIcon size={18} />공유</button>
                  <button type="button" onClick={() => setModal('export')}><Download size={18} />내보내기</button>
                  <button type="button" onClick={() => setModal('delete')}><Trash2 size={18} />삭제</button>
                </div>
              ) : null}
            </div>

            {!isFlowResult ? (
              <div className="ui-result-screen-tabs-row">
                <nav className="ui-result-screen-tabs" aria-label="화면 선택">
                  {screens.map((screen, index) => (
                    <button
                      key={`${screen.label}-${index}`}
                      type="button"
                      className={activeScreenIndex === index ? 'active' : ''}
                      onClick={() => setActiveScreenIndex(index)}
                    >
                      {screen.label || `화면 ${index + 1}`}
                    </button>
                  ))}
                </nav>
                {canOpenInsightReport ? (
                  <button
                    type="button"
                    className="ui-result-insight-report-btn"
                    onClick={handleOpenInsightReport}
                  >
                    <Sparkles size={16} />
                    인사이트 보고서 생성
                  </button>
                ) : null}
              </div>
            ) : (
              <div className="ui-result-screen-tabs-row ui-result-screen-tabs-row--flow">
                <div className="ui-result-divider" />
                {canOpenInsightReport ? (
                  <button
                    type="button"
                    className="ui-result-insight-report-btn"
                    onClick={handleOpenInsightReport}
                  >
                    <Sparkles size={16} />
                    인사이트 보고서 생성
                  </button>
                ) : null}
              </div>
            )}

            <section className="ui-result-section">
              <div className="ui-result-section__head">
                <h3>퍼소나 피드백</h3>
                {personaFeedbacks.length > FEEDBACK_PAGE_SIZE ? (
                  <div className="ui-result-feedback-pagination" aria-label="퍼소나 피드백 페이지">
                    <button
                      type="button"
                      onClick={() => setFeedbackPage((page) => Math.max(0, page - 1))}
                      disabled={normalizedFeedbackPage === 0}
                      aria-label="이전 퍼소나 피드백"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <div>
                      {Array.from({ length: feedbackPageCount }).map((_, index) => (
                        <button
                          key={index}
                          type="button"
                          className={index === normalizedFeedbackPage ? 'active' : ''}
                          onClick={() => setFeedbackPage(index)}
                          aria-label={`퍼소나 피드백 ${index + 1}페이지`}
                          aria-current={index === normalizedFeedbackPage ? 'page' : undefined}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => setFeedbackPage((page) => Math.min(feedbackPageCount - 1, page + 1))}
                      disabled={normalizedFeedbackPage >= feedbackPageCount - 1}
                      aria-label="다음 퍼소나 피드백"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                ) : null}
              </div>
              {personaFeedbacks.length ? (
                <div className="ui-result-feedback-grid">
                  {visiblePersonaFeedbacks.map((item) => (
                    <article key={item.id} className="ui-result-feedback-card">
                      <button
                        type="button"
                        className="ui-result-feedback-card__header"
                        onClick={() => handleOpenPersonaInfo(item.persona)}
                        aria-label={`${item.persona.name || '퍼소나'} 기본정보 보기`}
                      >
                        <PersonaAvatar persona={item.persona} size="sm" />
                        <div>
                          <strong>{item.persona.name}</strong>
                          {getPersonaSegmentLabel(item.persona) ? <PersonaTagChip tag={item.persona.tag} /> : null}
                        </div>
                        <ChevronRight size={16} />
                      </button>
                      <p>{item.summary}</p>
                      <dl>
                        {isFlowResult ? (
                          <>
                            <div><dt>혼란도</dt><dd className={getRiskMetricClass(item.confusion)}>{formatPlainScore(item.confusion)}</dd></div>
                            <div><dt>이탈위험</dt><dd className={getRiskMetricClass(item.dropoffRisk)}>{formatPlainScore(item.dropoffRisk)}</dd></div>
                            <div><dt>완료 가능성</dt><dd className={getCompletionMetricClass(item.completionRate)}>{formatPlainScore(item.completionRate)}</dd></div>
                            <div><dt>예상 위험 단계</dt><dd className="metric-negative">{item.riskStage}</dd></div>
                          </>
                        ) : (
                          <>
                            <div><dt>명확성</dt><dd className={getSingleScreenMetricClass(item.clarity)}>{formatPlainScore(item.clarity)}</dd></div>
                            <div><dt>사용성</dt><dd className={getSingleScreenMetricClass(item.usability)}>{formatPlainScore(item.usability)}</dd></div>
                            <div><dt>만족도</dt><dd className={getSingleScreenMetricClass(item.satisfaction)}>{formatPlainScore(item.satisfaction)}</dd></div>
                          </>
                        )}
                      </dl>
                      {!isFlowResult ? (
                        <footer>
                          <span className="positive">긍정 코멘트 {item.positiveCount}</span>
                          <span className="negative">부정 코멘트 {item.negativeCount}</span>
                        </footer>
                      ) : null}
                    </article>
                  ))}
                </div>
              ) : (
                <PersonaState title="퍼소나 피드백이 없습니다" message="테스트를 실행하면 퍼소나별 피드백이 표시됩니다." />
              )}
            </section>

            <section
              ref={evaluationSectionRef}
              className={`ui-result-section${isFlowResult ? ' ui-result-section--flow-evaluation' : ' ui-result-section--screen-evaluation'}`}
            >
              <div className={`ui-result-evaluation-panel${isFlowResult ? ' ui-result-evaluation-panel--flow' : ''}`}>
                {isFlowResult ? (
                  <div ref={evaluationFlowHeadRef} className="ui-result-evaluation-panel__head">
                    <h3>단계별 화면 평가</h3>
                    <nav className="ui-result-flow-steps" aria-label="플로우 화면 선택">
                      {screens.map((screen, index) => {
                        const hasRiskWarning = index === riskiestFlowScreenIndex;
                        return (
                          <React.Fragment key={`${screen.label}-${index}`}>
                            <button
                              type="button"
                              className={`${activeScreenIndex === index ? 'active' : ''}${hasRiskWarning ? ' has-risk-warning' : ''}`}
                              onClick={() => setActiveScreenIndex(index)}
                            >
                              <span>
                                {screen.image_url ? (
                                  <AuthenticatedScreenImage
                                    src={screen.image_url}
                                    alt=""
                                    fallbackText=""
                                  />
                                ) : null}
                                {hasRiskWarning ? (
                                  <em className="ui-result-flow-risk-icon" aria-label="예상 위험 단계">!</em>
                                ) : null}
                              </span>
                              <strong>{`Flow ${index + 1}`}</strong>
                            </button>
                            {index < screens.length - 1 ? <i aria-hidden="true" /> : null}
                          </React.Fragment>
                        );
                      })}
                    </nav>
                    <h3>화면 평가</h3>
                    <div className="ui-result-evaluation-head">
                      <div className="ui-result-comment-tabs">
                        <button type="button" className={commentFilter === 'all' ? 'active' : ''} onClick={() => setCommentFilter('all')}>전체 코멘트</button>
                        <button type="button" className={commentFilter === 'positive' ? 'active' : ''} onClick={() => setCommentFilter('positive')}>긍정</button>
                        <button type="button" className={commentFilter === 'negative' ? 'active' : ''} onClick={() => setCommentFilter('negative')}>부정</button>
                      </div>
                      <label className="ui-result-persona-filter">
                        <select aria-label="퍼소나 필터" value={personaFilter} onChange={(event) => setPersonaFilter(event.target.value)}>
                          <option value="all">모든 퍼소나</option>
                          {personaFeedbacks.map((item) => (
                            <option key={item.persona.id || item.id} value={String(item.persona.id || '')}>
                              {item.persona.name}
                            </option>
                          ))}
                        </select>
                        <ChevronDown size={16} />
                      </label>
                    </div>
                  </div>
                ) : (
                  <div ref={evaluationHeadRef} className="ui-result-evaluation-panel__head">
                    <h3>화면 평가</h3>
                    <div className="ui-result-evaluation-head">
                      <div className="ui-result-comment-tabs">
                        <button type="button" className={commentFilter === 'all' ? 'active' : ''} onClick={() => setCommentFilter('all')}>전체 코멘트</button>
                        <button type="button" className={commentFilter === 'positive' ? 'active' : ''} onClick={() => setCommentFilter('positive')}>긍정</button>
                        <button type="button" className={commentFilter === 'negative' ? 'active' : ''} onClick={() => setCommentFilter('negative')}>부정</button>
                      </div>
                      <label className="ui-result-persona-filter">
                        <select aria-label="퍼소나 필터" value={personaFilter} onChange={(event) => setPersonaFilter(event.target.value)}>
                          <option value="all">모든 퍼소나</option>
                          {personaFeedbacks.map((item) => (
                            <option key={item.persona.id || item.id} value={String(item.persona.id || '')}>
                              {item.persona.name}
                            </option>
                          ))}
                        </select>
                        <ChevronDown size={16} />
                      </label>
                    </div>
                  </div>
                )}
                <div className={isFlowResult ? 'ui-result-evaluation-panel__body' : undefined}>
                  <div className={`ui-result-evaluation-grid${isFlowResult ? ' ui-result-evaluation-grid--flow' : ''}`}>
                <article className="ui-result-screen-preview">
                  <strong>{activeScreen.label || `화면 ${activeScreenIndex + 1}`}</strong>
                  <div className="ui-result-screen-frame">
                    {activeScreen.image_url ? (
                      <div className="ui-result-screen-canvas">
                        <AuthenticatedScreenImage
                          src={activeScreen.image_url}
                          alt={activeScreen.label || `화면 ${activeScreenIndex + 1}`}
                          fallbackText={activeScreen.url || '인증된 이미지 요청에 실패했습니다.'}
                        />
                        {screenMarkerComments.map((comment) => (
                          <button
                            key={comment.id}
                            type="button"
                            className={`ui-result-screen-pin ${comment.type === 'positive' ? 'positive' : 'negative'}${selectedCommentId === comment.id ? ' is-selected' : ''}`}
                            style={markerPositionStyle(comment)}
                            aria-label={`${comment.label} 코멘트로 이동`}
                            aria-pressed={selectedCommentId === comment.id}
                            aria-controls={getCommentAnchorId(comment.id)}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={(event) => handleSelectCommentMarker(comment.id, event)}
                          >
                            {comment.markerNumber}
                          </button>
                        ))}
                      </div>
                    ) : activeScreen.figma_embed_url || activeScreen.figmaEmbedUrl ? (
                      <div className="ui-result-screen-canvas ui-result-screen-canvas--figma">
                        <iframe
                          className="ui-result-screen-figma"
                          src={activeScreen.figma_embed_url || activeScreen.figmaEmbedUrl}
                          title={activeScreen.label || `화면 ${activeScreenIndex + 1}`}
                        />
                        {screenMarkerComments.map((comment) => (
                          <button
                            key={comment.id}
                            type="button"
                            className={`ui-result-screen-pin ${comment.type === 'positive' ? 'positive' : 'negative'}${selectedCommentId === comment.id ? ' is-selected' : ''}`}
                            style={markerPositionStyle(comment)}
                            aria-label={`${comment.label} 코멘트로 이동`}
                            aria-pressed={selectedCommentId === comment.id}
                            aria-controls={getCommentAnchorId(comment.id)}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={(event) => handleSelectCommentMarker(comment.id, event)}
                          >
                            {comment.markerNumber}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="ui-result-screen-empty">
                        <strong>{activeScreen.url ? 'URL 화면 미리보기' : '화면 이미지가 없습니다'}</strong>
                        <p>{activeScreen.url || '연결된 이미지 소스가 없어서 코멘트 위치만 표시합니다.'}</p>
                        {screenMarkerComments.map((comment) => (
                          <button
                            key={comment.id}
                            type="button"
                            className={`ui-result-screen-pin ${comment.type === 'positive' ? 'positive' : 'negative'}${selectedCommentId === comment.id ? ' is-selected' : ''}`}
                            style={markerPositionStyle(comment)}
                            aria-label={`${comment.label} 코멘트로 이동`}
                            aria-pressed={selectedCommentId === comment.id}
                            aria-controls={getCommentAnchorId(comment.id)}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={(event) => handleSelectCommentMarker(comment.id, event)}
                          >
                            {comment.markerNumber}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </article>

                <aside className="ui-result-comments">
                  <h3>{`퍼소나 코멘트 (${displayComments.length})`}</h3>
                  <div ref={commentsScrollRef}>
                    {displayComments.length ? displayComments.map((comment) => (
                      <article
                        id={getCommentAnchorId(comment.id)}
                        key={comment.id}
                        ref={(node) => {
                          if (node) {
                            commentCardRefs.current[comment.id] = node;
                          } else {
                            delete commentCardRefs.current[comment.id];
                          }
                        }}
                        className={selectedCommentId === comment.id ? 'is-selected' : ''}
                        tabIndex={-1}
                        data-comment-id={comment.id}
                      >
                        <PersonaAvatar persona={comment.persona} size="sm" />
                        <div className={'persona-speach'}>
                          <header>
                            <strong>{comment.persona.name}</strong>
                            {!isFlowResult ? (
                              <span className={comment.type === 'positive' ? 'positive' : 'negative'}>
                                {comment.label}
                              </span>
                            ) : null}
                          </header>
                          <p>{comment.content}</p>
                        </div>
                      </article>
                    )) : (
                      <PersonaState title="표시할 코멘트가 없습니다" message="필터를 변경하거나 테스트 실행 후 다시 확인해 주세요." />
                    )}
                  </div>
                </aside>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </section>

        {modal === 'share' ? (
          <ModalFrame
            title="공유하기"
            description="아래 링크를 통해 접속하면 누구나 테스트 결과를 볼 수 있습니다."
            onClose={() => setModal(null)}
            footer={<button type="button" onClick={() => setModal(null)}>확인</button>}
          >
            <label className="ui-result-share-field">
              <span>공유링크</span>
              <div>
                <input value={shareUrl} readOnly />
                <button type="button" onClick={handleCopyShareUrl} aria-label="공유 링크 복사">
                  <Copy size={20} />
                </button>
              </div>
            </label>
            <small>{`${formatSharedAt()}에 공유됨`}</small>
          </ModalFrame>
        ) : null}

        {modal === 'export' ? (
          <ModalFrame
            title="내보내기"
            description="내보낼 파일 형식을 선택해주세요."
            onClose={() => setModal(null)}
            footer={(
              <>
                <button type="button" onClick={() => setModal(null)}>취소하기</button>
                <button type="button" onClick={handleExport}>내보내기</button>
              </>
            )}
          >
            <div className="ui-result-export-options">
              {EXPORT_OPTIONS.map((option) => (
                <label key={option.value} className={exportFormat === option.value ? 'selected' : ''}>
                  <input
                    type="radio"
                    name="exportFormat"
                    value={option.value}
                    checked={exportFormat === option.value}
                    onChange={(event) => setExportFormat(event.target.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </ModalFrame>
        ) : null}

        {modal === 'delete' ? (
          <DeleteTestModal
            deleting={deleting}
            onCancel={() => {
              if (deleting) return;
              setModal(null);
            }}
            onConfirm={handleDelete}
          />
        ) : null}

        {modal === 'persona-info' && selectedPersonaInfo ? (
          <PersonaInfoModal persona={selectedPersonaInfo} onClose={handleClosePersonaInfo} />
        ) : null}
        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast((current) => ({ ...current, isOpen: false }))}
        />
      </main>
    </UITestPageShell>
  );
}

export default UITestDetailPage;
